import Phaser from 'phaser';
import { Direction } from '@shared/types/entity';
import { getNPCTemplate } from '@shared/data/npcs';
import { getBodySpriteKey, getBodyIdleFrame } from '../../../sprites/BodySpriteManager';
import { getHeadSpriteKey, getHeadFrameForDirection } from '../../../sprites/HeadSpriteManager';

/**
 * Mini-retrato de un NPC (por templateId) encuadrado por su contenido opaco
 * dentro de un box cuadrado, mirando hacia abajo. Usa la cabeza si el NPC la
 * tiene (cara humanoide), si no el cuerpo (criaturas). Reutilizable por la
 * tarjeta de detalle del World Map (misma técnica que `QuestUI.addNpcMini`).
 *
 * Devuelve el sprite ya posicionado/escalado (scrollFactor 0), o null si no hay
 * textura. El caller lo agrega a su container y le fija el depth.
 */

// Cache del bbox opaco por textura+frame (getImageData es caro).
const bboxCache = new Map<string, { cx: number; cy: number; w: number; h: number; fw: number; fh: number }>();

function contentBBox(
  scene: Phaser.Scene,
  textureKey: string,
  frameIndex: number
): { cx: number; cy: number; w: number; h: number; fw: number; fh: number } | null {
  const cacheKey = `${textureKey}#${frameIndex}`;
  const cached = bboxCache.get(cacheKey);
  if (cached) return cached;
  const tex = scene.textures.get(textureKey);
  const frame = tex.get(frameIndex);
  if (!frame) return null;
  const fw = frame.cutWidth;
  const fh = frame.cutHeight;
  const canvas = document.createElement('canvas');
  canvas.width = fw;
  canvas.height = fh;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  const src = tex.getSourceImage() as CanvasImageSource;
  ctx.drawImage(src, frame.cutX, frame.cutY, fw, fh, 0, 0, fw, fh);
  const data = ctx.getImageData(0, 0, fw, fh).data;
  let x0 = fw, y0 = fh, x1 = -1, y1 = -1;
  for (let yy = 0; yy < fh; yy++) {
    for (let xx = 0; xx < fw; xx++) {
      if (data[(yy * fw + xx) * 4 + 3] > 20) {
        if (xx < x0) x0 = xx;
        if (xx > x1) x1 = xx;
        if (yy < y0) y0 = yy;
        if (yy > y1) y1 = yy;
      }
    }
  }
  const res =
    x1 < 0
      ? { cx: fw / 2, cy: fh / 2, w: fw, h: fh, fw, fh }
      : { cx: (x0 + x1 + 1) / 2, cy: (y0 + y1 + 1) / 2, w: x1 - x0 + 1, h: y1 - y0 + 1, fw, fh };
  bboxCache.set(cacheKey, res);
  return res;
}

export function createNpcPortrait(
  scene: Phaser.Scene,
  templateId: string,
  cx: number,
  cy: number,
  box: number
): Phaser.GameObjects.Sprite | null {
  const tpl = getNPCTemplate(templateId);
  if (!tpl) return null;
  const headId = tpl.headId ?? 0;
  const bodyId = tpl.bodyId ?? 1;
  let key: string;
  let frame: number;
  if (headId > 0 && scene.textures.exists(getHeadSpriteKey(headId))) {
    key = getHeadSpriteKey(headId);
    frame = getHeadFrameForDirection(Direction.DOWN);
  } else if (scene.textures.exists(getBodySpriteKey(bodyId))) {
    key = getBodySpriteKey(bodyId);
    frame = getBodyIdleFrame(Direction.DOWN, bodyId);
  } else {
    return null;
  }
  const spr = scene.add.sprite(cx, cy, key, frame).setOrigin(0.5).setScrollFactor(0);
  const bb = contentBBox(scene, key, frame);
  const scale = bb
    ? box / Math.max(1, Math.max(bb.w, bb.h))
    : box / Math.max(1, Math.max(spr.width, spr.height));
  spr.setScale(scale);
  if (bb) {
    spr.setPosition(cx - (bb.cx - bb.fw / 2) * scale, cy - (bb.cy - bb.fh / 2) * scale);
  }
  return spr;
}
